import type {
  MarketplaceListingData,
  MarketplaceListingItem,
} from "@/types/api/marketplace";
import type {
  ProductPagination,
  ProductSearchData,
  ProductSearchItem,
  ProductSearchResponse,
} from "@/types/api/marketplace-product";
import { transformProductFiltersFromAPI } from "./transform-product-filters";

function getCoverPhotoUrl(photos: ProductSearchItem["photos"]): string {
  const cover = photos.find((photo) => photo.is_cover) ?? photos[0];
  return cover?.url ?? "/images/trip-type-friends.png";
}

function formatProductDuration(item: ProductSearchItem): string {
  if (item.durationDays > 0) {
    return item.durationDays === 1 ? "1 Day" : `${item.durationDays} Days`;
  }
  if (item.durationHours > 0) {
    return item.durationHours === 1 ? "1 Hour" : `${item.durationHours} Hours`;
  }
  if (item.durationMinutes > 0) {
    return `${item.durationMinutes} Minutes`;
  }
  return "";
}

function buildResultsSummary(totalCount: number, searchQuery = ""): string {
  const countLabel = totalCount === 1 ? "1 result" : `${totalCount} results`;
  if (searchQuery.trim()) {
    return `${countLabel}: ${searchQuery.trim()}`;
  }
  return countLabel;
}

function formatMarketplaceListingLocation(
  item: Pick<ProductSearchItem, "city" | "country" | "location">
): string | undefined {
  const city = item.city?.trim() || item.location?.city?.trim();
  const country = item.country?.trim() || item.location?.country?.trim();
  const label = [city, country].filter(Boolean).join(", ");
  return label || undefined;
}

export function mapProductToListingItem(
  item: ProductSearchItem
): MarketplaceListingItem {
  return {
    id: item.uid,
    slug: item.slug,
    title: item.title,
    image: getCoverPhotoUrl(item.photos),
    badge: item.status === "LIVE" ? undefined : item.status,
    location: formatMarketplaceListingLocation(item),
    duration: formatProductDuration(item),
    rating: { score: 0, review_count: 0 },
    price: {
      currency: item.price.currency ?? "INR",
      value: item.price.value ?? 0,
    },
    href: `/experiences/${item.slug}`,
  };
}

export function mapProductSearchToListingData(
  apiData: ProductSearchData,
  searchQuery = ""
): MarketplaceListingData {
  const { dynamic_filters } = transformProductFiltersFromAPI(
    apiData.filters,
    apiData.keywords
  );

  return {
    page_title: "Activities",
    results_summary: buildResultsSummary(
      apiData.pagination.total_count,
      searchQuery
    ),
    total_count: apiData.pagination.total_count,
    dynamic_filters,
    listings: apiData.items.map(mapProductToListingItem),
    sections: [],
    search_suggestions: [],
  };
}

export interface MarketplaceListingQueryResult {
  listingData: MarketplaceListingData;
  filterMapping: Record<string, Record<number, string>>;
  pagination: ProductPagination;
}

export function mapProductSearchResponse(
  response: ProductSearchResponse,
  searchQuery = ""
): MarketplaceListingQueryResult {
  const apiData = response.data;
  const { filterMapping } = transformProductFiltersFromAPI(
    apiData.filters,
    apiData.keywords
  );

  return {
    listingData: mapProductSearchToListingData(apiData, searchQuery),
    filterMapping,
    pagination: apiData.pagination,
  };
}
